// Barracks / Stable / Workshop — the live game's recruit screen in the redesign's dress (Ben, Oct 8 2026, from his screen
// recording of the app). One swipeable strip of every unit, Barracks → Stable → Workshop, the way the app has it: the title
// band follows the unit's building; the neighbours' names and stationed counts sit either side of the unit's name (locked
// units struck through); the stats sit beside the cost, slider and RECRUIT; a gold chip on the painting counts that
// building's training queue and drops the queue over the art. A locked unit shows "Requires X Research (Level 1) ›" (the
// server's own prerequisite text) and leads to the Forge; a unit whose building isn't built leads to construction mode.
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/api/client";
import type { TroopDef, Village } from "@/api/types";
import { art } from "@/kit/art";
import { AmountSlider, CostLine, Countdown, LockSeal, TierDisc, TitleBand, UnitMedal } from "@/kit/city";
import { IconChevronLeft, IconChevronRight, IconClose, IconHourglass, IconInfo, IconShield, IconSwords } from "@/kit/icons";
import { Confirm } from "@/kit/overlays";
import { Bar, Button, Disc, cx } from "@/kit/primitives";
import { useNow } from "@/state/clock";
import { usePrefs } from "@/state/prefs";
import { usePrompts } from "@/state/prompts";
import { TIER_NUMERALS, TROOP_CLASS, TROOP_KEYS, TROOP_NAMES, TROOP_PLURAL, TROOP_SLUG, type BuildingKey, type TroopKey } from "@/game/constants";
import { fmtNum, plural } from "@/game/format";
import { fmtDuration } from "@/game/time";
import { UNITS, unitStats } from "@/game/rules";
import { buildingMap, freePopulation, homeCount, recruitDone, recruitQueue, researchLevel, troopCost, troopDefs, type RecruitEntry } from "@/game/city";
import { CityPage, useCity } from "./CityRoute";
import { buildLink } from "./CityView";
import "./recruit.css";

const RECRUIT_BUILDINGS: BuildingKey[] = ["b_b", "b_st", "b_wo"];
const TITLE: Record<string, string> = { b_b: "Barracks", b_st: "Stable", b_wo: "Workshop" };

interface Slot { def: TroopDef; bk: BuildingKey }

/** Why a unit can't be recruited here, or null when it can. */
export type RecruitBlock = { kind: "build" | "research" | "prereq"; text: string } | null;

export function recruitBlock(v: Village, slot: Slot): RecruitBlock {
  const built = (buildingMap(v)[slot.bk]?.level ?? 0) > 0;
  if (!built) return { kind: "build", text: `Requires ${TITLE[slot.bk]} (Level 1)` };
  const rl = researchLevel(v, slot.def.key);
  const server = (slot.def.prereqs_string ?? "").trim();
  const useful = /^requires?\b/i.test(server) && server.length > 9 ? server : "";
  if (rl === 0) return { kind: "research", text: useful || `Requires ${TROOP_NAMES[slot.def.key]} Research (Level 1)` };
  if (!slot.def.has_prereqs) return { kind: "prereq", text: useful || "Requirements not met" };
  return null;
}

/** The longest bar on each stat: the best of every unit at research III (speed: the fastest fills the bar). */
const STAT_MAX = (() => {
  const all = TROOP_KEYS.filter((k) => k !== "t_n").map((k) => unitStats(k, 3));
  return {
    att: Math.max(...all.map((s) => s.att)),
    infdef: Math.max(...all.map((s) => s.infdef)),
    cavdef: Math.max(...all.map((s) => s.cavdef)),
    haul: Math.max(...all.map((s) => s.haul)),
    fastest: Math.min(...TROOP_KEYS.filter((k) => k !== "t_n").map((k) => unitStats(k, 1).speed).filter((n) => n > 0)),
  };
})();

const reduceMotion = () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

export function Recruit() {
  const { building } = useParams();
  const [params] = useSearchParams();
  const { v, wid, vid, actions, base } = useCity();
  const nav = useNavigate();
  const bk = building as BuildingKey;
  const wantKey = params.get("u") as TroopKey | null;

  const slots = useMemo<Slot[]>(() => (v ? RECRUIT_BUILDINGS.flatMap((b) => troopDefs(v, b).map((def) => ({ def, bk: b }))) : []), [v]);
  const slotKeys = slots.map((s) => `${s.bk}.${s.def.key}`).join(",");
  // the unit the URL asks for; else the building's first unit (the app opens each building on its first unit)
  const wantIdx = useMemo(() => {
    if (!slots.length) return -1;
    let i = wantKey ? slots.findIndex((s) => s.def.key === wantKey) : -1;
    if (i < 0) i = slots.findIndex((s) => s.bk === bk);
    return Math.max(0, i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slotKeys, wantKey, bk]);

  const trackRef = useRef<HTMLDivElement>(null);
  const [idx, setIdx] = useState(-1); // the unit in view, live while swiping
  const settledRef = useRef(-1); // the unit the strip last came to rest on (the URL follows it)
  const settleTimer = useRef<number | undefined>(undefined);
  const [amount, setAmount] = useState(0);
  const [queueOpen, setQueueOpen] = useState(false);
  const [cancel, setCancel] = useState<RecruitEntry | null>(null);
  const videoOn = usePrefs((s) => s.recruitVideo); // Settings › Display › Recruit videos

  // first paint: jump straight to the unit, no animation
  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el || wantIdx < 0 || settledRef.current >= 0) return;
    el.scrollLeft = wantIdx * el.clientWidth;
    settledRef.current = wantIdx;
    setIdx(wantIdx);
  }, [wantIdx]);

  const goTo = useCallback((i: number, smooth = true) => {
    const el = trackRef.current;
    if (!el || i < 0 || i >= el.children.length) return;
    el.scrollTo({ left: i * el.clientWidth, behavior: smooth && !reduceMotion() ? "smooth" : "auto" });
  }, []);

  // the URL changed from outside (a link, back/forward): glide to that unit
  useEffect(() => {
    if (wantIdx >= 0 && settledRef.current >= 0 && wantIdx !== settledRef.current) goTo(wantIdx);
  }, [wantIdx, goTo]);

  useEffect(() => () => window.clearTimeout(settleTimer.current), []);

  const onScroll = () => {
    const el = trackRef.current;
    if (!el || !el.clientWidth) return;
    const i = Math.max(0, Math.min(slots.length - 1, Math.round(el.scrollLeft / el.clientWidth)));
    if (i !== idx) {
      setIdx(i);
      setAmount(0);
    }
    window.clearTimeout(settleTimer.current);
    settleTimer.current = window.setTimeout(() => {
      const at = Math.max(0, Math.min(slots.length - 1, Math.round(el.scrollLeft / el.clientWidth)));
      if (at === settledRef.current) return;
      settledRef.current = at;
      const s = slots[at];
      if (s && (s.bk !== bk || s.def.key !== wantKey)) nav(`${base}/recruit/${s.bk}?u=${s.def.key}`, { replace: true });
    }, 140);
  };

  const onKey = (e: KeyboardEvent) => {
    if (e.key === "ArrowRight") { e.preventDefault(); goTo(idx + 1); }
    else if (e.key === "ArrowLeft") { e.preventDefault(); goTo(idx - 1); }
  };

  if (!RECRUIT_BUILDINGS.includes(bk)) return <Navigate to={base} replace />;
  if (!v) return <CityPage layout="col">{null}</CityPage>;

  const cur = slots[idx] ?? slots[wantIdx];
  const curBk = cur?.bk ?? bk;
  const level = buildingMap(v)[curBk]?.level ?? 0;
  const queue = recruitQueue(v, curBk);

  return (
    <CityPage layout="col">
      <TitleBand title={TITLE[curBk]} sub={level ? `Level ${level}` : "Not built"} onBack={() => nav(base)} right="gear" onRight={() => nav(`${base}/hall`)} rightLabel="City overview" />

      <section className="frame plain rc" aria-label="Recruit">
        <div className="rc-stage">
          <div ref={trackRef} className="rc-track" onScroll={onScroll} onKeyDown={onKey} tabIndex={0} role="region" aria-roledescription="carousel" aria-label="Units — swipe or use the arrow keys">
            {slots.map((s, i) => {
              const rl = researchLevel(v, s.def.key);
              return <Slide key={`${s.bk}.${s.def.key}`} troop={s.def.key} tier={rl} locked={!!recruitBlock(v, s)} index={i} total={slots.length} current={i === idx} near={Math.abs(i - idx) <= 1} video={videoOn} />;
            })}
          </div>
          {cur && (
            <>
              <button type="button" className={cx("rc-qchip", queueOpen && "on", queue.length > 0 && "busy")} onClick={() => setQueueOpen((o) => !o)} aria-expanded={queueOpen}
                aria-label={`${TITLE[curBk]} training queue: ${queue.length}`}>
                <IconHourglass /><b>{queue.length}</b>
              </button>
              {idx > 0 && <button type="button" className="rc-edge prev" onClick={() => goTo(idx - 1)} aria-label={`Previous unit: ${TROOP_NAMES[slots[idx - 1].def.key]}`}><IconChevronLeft /></button>}
              {idx < slots.length - 1 && <button type="button" className="rc-edge next" onClick={() => goTo(idx + 1)} aria-label={`Next unit: ${TROOP_NAMES[slots[idx + 1].def.key]}`}><IconChevronRight /></button>}
              <div className="rc-dots" aria-hidden="true">
                {RECRUIT_BUILDINGS.map((b) => (
                  <span key={b} className="grp">
                    {slots.map((s, i) => (s.bk === b ? <i key={i} className={cx(i === idx && "on")} /> : null))}
                  </span>
                ))}
              </div>
              <QueuePanel open={queueOpen} title={TITLE[curBk]} queue={queue} v={v} onClose={() => setQueueOpen(false)} onCancel={setCancel} />
            </>
          )}
        </div>

        {cur && nameBand()}
        {cur && body(cur)}
      </section>

      <Confirm
        open={!!cancel}
        onClose={() => setCancel(null)}
        title="Cancel training?"
        text={cancel ? `The remaining ${fmtNum(cancel.item.num_units - recruitDone(cancel, Date.now()))} ${TROOP_PLURAL[cancel.troop]} are cancelled and their resources come back. Units already trained stay.` : ""}
        action="Cancel training"
        busy={actions.isBusy("cancel_recruit")}
        onConfirm={async () => {
          if (!cancel) return;
          const r = await actions.run("cancel_recruit", () => api.troops.cancelRecruit({ wid, vid }, cancel.item.id, cancel.building));
          if (r) setCancel(null);
        }}
      />
    </CityPage>
  );

  // render functions, not inner components: an inner component is a new type on every render (the clock ticks each
  // second), so React would remount it — images flash on iOS Safari and a focused field loses its keyboard.
  function nameBand() {
    const at = idx >= 0 ? idx : wantIdx;
    const prev = slots[at - 1];
    const next = slots[at + 1];
    const c = slots[at];
    const struck = (s: Slot) => !!recruitBlock(v!, s);
    const side = (s: Slot | undefined, dir: "prev" | "next") =>
      s ? (
        <button type="button" className={cx("rc-nb", dir)} onClick={() => goTo(at + (dir === "prev" ? -1 : 1))} aria-label={`${dir === "prev" ? "Previous" : "Next"}: ${TROOP_NAMES[s.def.key]}, ${fmtNum(homeCount(v!, s.def.key))} stationed`}>
          <b className={cx(struck(s) && "struck")}>{TROOP_NAMES[s.def.key]}</b>
          <span>{fmtNum(homeCount(v!, s.def.key))}</span>
        </button>
      ) : <span className={cx("rc-nb", dir, "none")} />;
    return (
      <div className="rc-names">
        {side(prev, "prev")}
        <div className="rc-cur">
          <div className="rc-cur-h">
            <Disc size="sm" label={`About the ${TROOP_NAMES[c.def.key]}`} onClick={() => usePrompts.getState().dialogShow(TROOP_NAMES[c.def.key], `${TROOP_CLASS[c.def.key]} unit, recruited at the ${TITLE[c.bk]}. Population ${UNITS[c.def.key].pop} each; moves ${UNITS[c.def.key].speed} minutes per field. Research at the Forge raises attack and defence (levels I–III).`)}><IconInfo /></Disc>
            <h2 className={cx(struck(c) && "struck")}>{TROOP_NAMES[c.def.key]}</h2>
            <span className="rc-cur-sp" aria-hidden="true" />
          </div>
          <p>Stationed: <b>{fmtNum(homeCount(v!, c.def.key))}</b></p>
        </div>
        {side(next, "next")}
      </div>
    );
  }

  function body(s: Slot) {
    const def = s.def;
    const rl = researchLevel(v!, def.key);
    const st = unitStats(def.key, rl);
    const block = recruitBlock(v!, s);
    const cost = troopCost(def);
    const perMs = cost.time ?? 0;
    const maxRes = Math.min(...(["r_w", "r_c", "r_i"] as const).map((r) => (cost[r] > 0 ? Math.floor((v!.resources?.[r] ?? 0) / cost[r]) : Infinity)));
    const maxPop = cost.p_u ? Math.floor(freePopulation(v!) / cost.p_u) : Infinity;
    const max = Math.max(0, Math.min(maxRes, maxPop, 99999));
    const n = Math.min(amount, max);
    const short = Object.fromEntries((["r_w", "r_c", "r_i"] as const).filter((r) => cost[r] > (v!.resources?.[r] ?? 0)).map((r) => [r, 1]));
    const stat = (cls: string, icon: React.ReactNode, label: string, value: number, fill: number) => (
      <div className={cx("rc-stat", cls)} title={label} role="listitem" aria-label={`${label} ${fmtNum(value)}`}>
        <span className="ico">{icon}</span>
        <span className="rc-bar"><i style={{ width: `${Math.round(Math.max(0, Math.min(1, fill)) * 100)}%` }} /><b>{fmtNum(value)}</b></span>
      </div>
    );
    return (
      <div className="rc-body">
        <div className="rc-stats" role="list" aria-label={`${TROOP_NAMES[def.key]} at research ${TIER_NUMERALS[Math.max(1, rl)]}`}>
          {stat("att", <IconSwords />, "Attack", st.att, st.att / STAT_MAX.att)}
          {stat("inf", <IconShield />, "Defence against infantry", st.infdef, st.infdef / STAT_MAX.infdef)}
          {stat("cav", <IconShield />, "Defence against cavalry", st.cavdef, st.cavdef / STAT_MAX.cavdef)}
          {stat("spd", <IconBolt />, "Speed (minutes per field)", st.speed, st.speed ? STAT_MAX.fastest / st.speed : 0)}
          {stat("hau", <IconSack />, "Carry", st.haul, st.haul / STAT_MAX.haul)}
        </div>
        <div className="rc-act">
          <CostLine cost={cost} time={perMs} pop={cost.p_u} short={block ? undefined : short} className="rc-cost" />
          {block ? (
            <button type="button" className="rc-req" onClick={() => nav(block.kind === "research" ? `${base}/forge` : buildLink(base, s.bk))}>
              <LockSeal size="sm" />
              <span>{block.text}</span>
              <IconChevronRight />
            </button>
          ) : (
            <>
              <AmountSlider value={n} max={max} onChange={setAmount} label={`${TROOP_PLURAL[def.key]} to recruit`} maxHint={false} />
              <Button kind="crimson" wide disabled={n <= 0} busy={actions.isBusy("recruit")}
                onClick={async () => {
                  const r = await actions.run("recruit", () => api.troops.recruit({ wid, vid }, def.key, n), { success: `${plural(n, TROOP_NAMES[def.key], TROOP_PLURAL[def.key])} in training.` });
                  if (r) setAmount(0);
                }}>
                Recruit{n > 0 ? ` ${fmtNum(n)}` : ""}
              </Button>
              <div className="rc-note">
                {max === 0
                  ? maxPop === 0 ? "No free population — upgrade the Farm." : `Not enough resources for one ${TROOP_NAMES[def.key]}.`
                  : n > 0
                    ? <CostLine size="sm" cost={{ r_w: cost.r_w * n, r_c: cost.r_c * n, r_i: cost.r_i * n }} pop={(cost.p_u ?? 0) * n} time={perMs * n} />
                    : <>Up to <b>{fmtNum(max)}</b>{queue.length ? " · joins the queue" : ""}</>}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }
}

// ------------------------------------------------------------------ one painting in the strip

// With recruit videos on (the default), a unit that has a clip plays it from the start each time it comes into view — on
// opening the screen and on every swipe to it — then rests on its last frame; a tap on it plays it again. The clips are
// one-off action shots (they don't loop cleanly), so they play once rather than loop. Only the unit in view plays; the
// others sit on their first frame (the poster), and only the unit in view and its neighbours preload. Muted + inline so
// phones autoplay it; where autoplay is refused (iOS Low Power Mode) the poster shows and a tap plays it.
const Slide = memo(function Slide({ troop, tier, locked, index, total, current, near, video }: { troop: TroopKey; tier: number; locked: boolean; index: number; total: number; current: boolean; near: boolean; video: boolean }) {
  const slug = TROOP_SLUG[troop];
  const clip = video ? art.unitVideos[slug] : undefined;
  const src = art.unitArt(slug, Math.max(1, tier));
  const alt = `${TROOP_NAMES[troop]}${tier ? ` at research ${TIER_NUMERALS[tier]}` : ""}`;
  const ref = useRef<HTMLVideoElement>(null);
  const play = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.muted = true;
    try { el.currentTime = 0; } catch { /* not seekable yet: it starts at 0 anyway */ }
    void el.play()?.catch(() => { /* autoplay refused: the poster stays, a tap plays it */ });
  }, []);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (current) play();
    else {
      el.pause();
      try { el.currentTime = 0; } catch { /* nothing loaded yet */ }
    }
  }, [current, clip, play]);
  return (
    <div className={cx("rc-slide", locked && "locked", clip && "has-video")} role="group" aria-roledescription="slide" aria-label={`${index + 1} of ${total}: ${TROOP_NAMES[troop]}`} aria-hidden={!current}>
      {clip ? (
        <>
          <img className="bg" src={clip.poster} alt="" decoding="async" draggable={false} />
          <video ref={ref} className="fg" src={clip.src} poster={clip.poster} muted playsInline preload={near ? "auto" : "none"} disablePictureInPicture disableRemotePlayback
            style={{ aspectRatio: `${clip.w}/${clip.h}` }} aria-label={alt} onClick={play} data-unit={slug} />
        </>
      ) : (
        <>
          <img className="bg" src={src} alt="" decoding="async" draggable={false} />
          <img className="fg" src={src} alt={alt} decoding="async" draggable={false} />
        </>
      )}
      {locked ? <span className="rc-lock"><LockSeal size="lg" /></span> : tier > 0 ? <TierDisc tier={tier} className="rc-tier" /> : null}
      <span className="rc-klass">{TROOP_CLASS[troop]}</span>
    </div>
  );
});

// ------------------------------------------------------------------ the building's training queue, dropped over the art

function QueuePanel({ open, title, queue, v, onClose, onCancel }: { open: boolean; title: string; queue: RecruitEntry[]; v: Village; onClose: () => void; onCancel: (e: RecruitEntry) => void }) {
  return (
    <div className={cx("rc-queue", open && "open")} aria-hidden={!open} inert={!open}>
      <div className="rc-queue-h">
        <h3>{title} queue</h3>
        <span className="muted">{queue.length ? plural(queue.length, "order", "orders") : "empty"}</span>
        <Disc size="sm" label="Close the queue" onClick={onClose}><IconClose /></Disc>
      </div>
      {!queue.length ? (
        <p className="rc-queue-empty">Nothing training — the yard is quiet.</p>
      ) : (
        <div className="rc-queue-rows">
          {queue.map((e, i) => <QueueRow key={`${e.troop}-${e.item.id}-${i}`} e={e} first={i === 0} v={v} onCancel={onCancel} />)}
        </div>
      )}
    </div>
  );
}

function QueueRow({ e, first, v, onCancel }: { e: RecruitEntry; first: boolean; v: Village; onCancel: (e: RecruitEntry) => void }) {
  const now = useNow();
  const per = e.item.unit_duration_ms || 0;
  const done = first ? recruitDone(e, now) : (e.item.completed_units ?? 0);
  const left = Math.max(0, e.item.num_units - done);
  const exact = first && per > 0 && e.item.last_updated_at_ms ? (e.item.completed_units ?? 0) + (now - e.item.last_updated_at_ms) / per : done;
  const nextIn = first && per > 0 ? (1 - (exact % 1)) * per : 0;
  return (
    <div className={cx("rc-qrow", first && "first")}>
      <UnitMedal troop={e.troop} tier={researchLevel(v, e.troop)} size={50} />
      <div className="txt">
        <b>{fmtNum(first ? left : e.item.num_units)} {TROOP_PLURAL[e.troop] ?? e.name}</b>
        <Bar value={e.item.num_units ? exact / e.item.num_units : 0} />
        <span className="meta">{first ? (done > 0 || exact > 0 ? `Next in ${fmtDuration(nextIn)}` : "Starting") : `Waiting · ${fmtDuration(Math.max(0, e.endMs - e.startMs))}`}</span>
      </div>
      <div className="right">
        {first ? <Countdown endMs={e.endMs} className="val" /> : <span className="val muted">{fmtDuration(Math.max(0, e.endMs - e.startMs))}</span>}
        <Disc size="sm" label={`Cancel ${fmtNum(e.item.num_units)} ${TROOP_PLURAL[e.troop]}`} onClick={() => onCancel(e)}><IconClose /></Disc>
      </div>
    </div>
  );
}

const IconBolt = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M13.5 2.5L5 13.5h6l-1 8 8.5-11h-6z" />
  </svg>
);
const IconSack = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 3.5h6l-1.8 3.2M9 3.5l1.8 3.2M10.8 6.7h2.4M10.8 6.7C6.5 9 4.5 13 4.5 16.2c0 2.6 2.4 4.3 7.5 4.3s7.5-1.7 7.5-4.3c0-3.2-2-7.2-6.3-9.5" />
    <path d="M10 13.5q2 1.6 4 0" />
  </svg>
);
